<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Cadastrar Novo Paciente</h4>
            <p class="card-title-desc">
              Preencha os dados do paciente. Campos marcados com 
              <span class="text-danger">*</span> são obrigatórios.
            </p>
            
            <form class="form-horizontal" role="form" @submit.prevent="submitForm">
              <div class="row">
                <!-- Coluna 1 -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="name" class="form-label">Nome Completo *</label>
                    <b-form-input
                      id="name"
                      v-model="form.name"
                      type="text"
                      :class="{ 'is-invalid': $v.form.name.$error }"
                      placeholder="Digite o nome completo do paciente"
                      required
                    />
                    <div v-if="$v.form.name.$error" class="invalid-feedback">
                      <span v-if="!$v.form.name.required">Nome é obrigatório</span>
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="birth_date" class="form-label">Data de Nascimento *</label>
                    <b-form-input
                      id="birth_date"
                      v-model="form.birth_date"
                      type="date"
                      :class="{ 'is-invalid': $v.form.birth_date.$error }"
                      required
                    />
                    <div v-if="$v.form.birth_date.$error" class="invalid-feedback">
                      <span v-if="!$v.form.birth_date.required">Data de nascimento é obrigatória</span>
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="sex" class="form-label">Sexo *</label>
                    <select
                      id="sex"
                      v-model="form.sex"
                      class="form-control"
                      :class="{ 'is-invalid': $v.form.sex.$error }"
                      required
                    >
                      <option value="">Selecione o sexo</option>
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                      <option value="O">Outro</option>
                    </select>
                    <div v-if="$v.form.sex.$error" class="invalid-feedback">
                      <span v-if="!$v.form.sex.required">Sexo é obrigatório</span>
                    </div>
                  </div>
                </div>

                <!-- Coluna 2 -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="cpf" class="form-label">CPF *</label>
                    <b-form-input
                      id="cpf"
                      v-model="form.cpf"
                      type="text"
                      :class="{ 'is-invalid': $v.form.cpf.$error }"
                      placeholder="000.000.000-00"
                      @input="formatCpf"
                    />
                    <div v-if="$v.form.cpf.$error" class="invalid-feedback">
                      <span v-if="!$v.form.cpf.required">CPF é obrigatório</span>
                      <span v-if="!$v.form.cpf.validCpf">CPF deve ser válido</span>
                    </div>
                  </div>

                  <div class="form-group">
                    <label for="sus_card_number" class="form-label">Cartão SUS</label>
                    <b-form-input
                      id="sus_card_number"
                      v-model="form.sus_card_number"
                      type="text"
                      placeholder="Digite o número do cartão SUS"
                    />
                    <small class="form-text text-muted">Campo opcional</small>
                  </div>

                  <div class="form-group">
                    <label for="phone" class="form-label">Telefone</label>
                    <b-form-input
                      id="phone"
                      v-model="form.phone"
                      type="tel"
                      placeholder="(11) 99999-9999"
                      @input="formatPhone"
                    />
                    <small class="form-text text-muted">Campo opcional</small>
                  </div>
                </div>
              </div>

              <!-- Botões de Ação -->
              <div class="row mt-4">
                <div class="col-12">
                  <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-secondary me-2" @click="resetForm">
                      <i class="bx bx-refresh"></i> Limpar Formulário
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary ml-3"
                      :disabled="loading || $v.form.$invalid"
                    >
                      <i v-if="loading" class="bx bx-loader bx-spin"></i>
                      <i v-else class="bx bx-save"></i>
                      {{ loading ? 'Cadastrando...' : 'Cadastrar Paciente' }}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { patientService } from "@/api/patients";
import { required } from "vuelidate/lib/validators";

// CPF validation function
const validCpf = (value) => {
  if (!value) return true; // Optional field
  const cpf = value.replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  
  // Check for known invalid sequences
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  
  // Validate check digits
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) return false;
  
  return true;
};

export default {
  page: {
    title: "Cadastro de Paciente",
    meta: [{ name: "description", content: "Formulário para cadastro de novo paciente no sistema" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Cadastro de Paciente",
      items: [
        {
          text: "Pacientes",
          href: "/patients",
        },
        {
          text: "Cadastrar Novo Paciente",
          active: true,
        },
      ],
      form: {
        name: "",
        birth_date: "",
        sex: "",
        cpf: "",
        sus_card_number: "",
        phone: "",
      },
      loading: false,
    };
  },
  validations: {
    form: {
      name: { required },
      birth_date: { required },
      sex: { required },
      cpf: { required, validCpf },
    },
  },
  methods: {
    async submitForm() {
      this.$v.$touch();
      
      if (this.$v.$invalid) {
        this.$toast.error("Por favor, corrija os erros no formulário");
        return;
      }

      this.loading = true;
      try {
        const patientData = {
          ...this.form,
          cpf: this.form.cpf.replace(/\D/g, ''), // Remove formatting
          phone: this.form.phone ? this.form.phone.replace(/\D/g, '') : null,
        };
        
        await patientService.createPatient(patientData);
        this.$toast.success("Paciente cadastrado com sucesso!");
        this.resetForm();
        this.$router.push("/patients");
      } catch (error) {
        console.error("Erro ao cadastrar paciente:", error);
        this.$toast.error(error.response?.data?.detail || "Erro ao cadastrar paciente");
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        name: "",
        birth_date: "",
        sex: "",
        cpf: "",
        sus_card_number: "",
        phone: "",
      };
      this.$v.$reset();
    },

    formatCpf() {
      let value = this.form.cpf.replace(/\D/g, '');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      this.form.cpf = value;
    },

    formatPhone() {
      let value = this.form.phone.replace(/\D/g, '');
      value = value.replace(/(\d{2})(\d)/, '($1) $2');
      value = value.replace(/(\d{4,5})(\d{4})$/, '$1-$2');
      this.form.phone = value;
    },
  },
};
</script>
